import { connect } from 'react-redux';
import * as ComponentFunctions from './ChildConsumption.List.Functions';
import { useEffect, useMemo, useState } from 'react';
import { createTableColumn, TableColumnDefinition, DataGrid, DataGridHeader, DataGridRow, DataGridHeaderCell, DataGridBody, DataGridCell, Spinner } from '@fluentui/react-components';
import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import ConsumptionDetailsCompoment from '../ConsumptionDetails/ConsumptionDetails.Component';
import { useTranslation } from 'react-i18next';
import * as ConsumptionListFunctions from '../ConsumptionList/ConsumptionList.Functions';

// styles
import './ChildConsumption.List.Styles.css';
// La paleta de .status-mark vive en la hoja de ConsumptionList; se importa
// explicitamente en vez de confiar en que otra pantalla ya la haya cargado.
import '../ConsumptionList/ConsumptionList.Styles.css';

const getNetAmountForGrid = (item: ProtocolConsumption): number => {
  if (item.totalAmountWithWithholding !== undefined && item.totalAmountWithWithholding !== null) {
    return item.totalAmountWithWithholding;
  }

  const mainNet = (item.amount ?? 0) - (item.withHolingAmount ?? 0);
  const conceptValuesNet = (item.conceptValues ?? []).reduce((acc, cv) => {
    const conceptGross = cv.value ?? 0;
    const conceptWithholding = cv.applyWithholding ? (cv.withholdingAmount ?? 0) : 0;
    return acc + (conceptGross - conceptWithholding);
  }, 0);

  return mainNet + conceptValuesNet;
};

// ─── Formato ─────────────────────────────────────────────────────────────────

const currencyCodeOf = (currency: string | undefined): string => {
  const normalized = (currency ?? '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : 'ARS';
};

/** Importe sin simbolo: la moneda se declara una sola vez en la franja superior. */
const fmtAmount = (value: number | undefined): string =>
  (value ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCurrency = (value: number | undefined, currency: string | undefined): string => {
  // Intl lanza si el codigo no es ISO de 3 letras.
  const safeCurrency = currencyCodeOf(currency);
  try {
    return (value ?? 0).toLocaleString('es-AR', { style: 'currency', currency: safeCurrency });
  } catch {
    return (value ?? 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
  }
};

const fmtPercent = (value: number | undefined): string =>
  `${(value ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}%`;

// ─── Celdas ──────────────────────────────────────────────────────────────────

const NumCell = (props: { value?: string; note?: string; tone?: 'strong' | 'negative' }) => {
  if (!props.value) {
    return <span className="cgrid-num-empty">—</span>;
  }

  return (
    <div className={`cgrid-num${props.tone ? ` cgrid-num--${props.tone}` : ''}`}>
      <span className="cgrid-num-value">{props.value}</span>
      {props.note && <span className="cgrid-num-note">{props.note}</span>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ChildConsumptionListComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  // No need to fetch visit-specific data; list derives from protocol consumptions filtered by ParentGroupingId
  useEffect(() => {
    if (props.ProtocolId && (props.protocol === null || props.protocol === undefined)) {
      // Ensure protocol is loaded
      props.GetMedicalProtocolById(props.ProtocolId);
    }
    // Also request consumptions filtered by ParentGroupingId from server
    if (props.ProtocolId && props.ParentGroupingId) {
      props.GetAllProtocolConsumptionsByProtocol(
        props.ProtocolId,
        1,
        100,
        {
          SelectedConsumptions: [],
          SelectedProtocolPatients: [],
          SelectedContractConcepts: [],
          SelectedCurrencys: '',
          SelectedContractStages: [],
          SelectedPractitioners: [],
          SelectedStatus: [],
          SelectedProtocols: [],
          SelectedSettlements: [],
          SelectedDateRange: [undefined, undefined],
          FilterText: undefined,
          showGroupedByVisit: false,
          GroupId: props.ParentGroupingId,
        }
      );
    }
  }, [props.ProtocolId, props.ParentGroupingId]);

  const [selectedConsumption, setSelectedConsumption] = useState<ProtocolConsumption | null>(null);
  const [isInformationPanelOpen, setIsInformationPanelOpen] = useState(false);

  const onInformationPanelDismiss = () => {
    setSelectedConsumption(null);
    setIsInformationPanelOpen(false);
  };

  const openConsumption = (item: ProtocolConsumption) => {
    setSelectedConsumption(item);
    setIsInformationPanelOpen(true);
  };

  const items = props.Consumptions ?? [];

  // Si todas las filas comparten moneda se declara una vez arriba y las celdas
  // quedan sin simbolo, que es lo que permite alinear los decimales en columna.
  // Con monedas mezcladas cada importe conserva su simbolo.
  const sharedCurrency = useMemo(() => {
    const codes = new Set(items.map((item) => currencyCodeOf(item.currencyCode)));
    return codes.size === 1 ? Array.from(codes)[0] : undefined;
  }, [items]);

  const money = (value: number | undefined, item: ProtocolConsumption): string =>
    sharedCurrency ? fmtAmount(value) : fmtCurrency(value, item.currencyCode);


  // Una columna por concepto liquidado, igual que en la grilla principal de
  // consumos: sin esto los conceptValues no se ven en ningun lado de esta
  // grilla. Se agrupan por nombre porque el mismo concepto puede venir con
  // distinto contractConceptId entre adendas.
  const conceptColumns = useMemo<TableColumnDefinition<ProtocolConsumption>[]>(() => {
    const conceptNames = new Set<string>();
    items.forEach((item) => {
      (item.conceptValues ?? []).forEach((conceptValue) => {
        if (conceptValue?.contractConceptName) {
          conceptNames.add(conceptValue.contractConceptName);
        }
      });
    });

    const totalFor = (item: ProtocolConsumption, conceptName: string) =>
      (item.conceptValues ?? [])
        .filter((conceptValue) => conceptValue.contractConceptName === conceptName)
        .reduce((sum, conceptValue) => sum + (conceptValue.value ?? 0), 0);

    return Array.from(conceptNames)
      .sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }))
      .map((conceptName) =>
        createTableColumn<ProtocolConsumption>({
          columnId: `concept-value-${conceptName}`,
          compare: (a, b) => totalFor(a, conceptName) - totalFor(b, conceptName),
          renderHeaderCell: () => <span title={conceptName}>{conceptName}</span>,
          renderCell: (item: ProtocolConsumption) => {
            const matches = (item.conceptValues ?? []).filter(
              (conceptValue) => conceptValue.contractConceptName === conceptName
            );
            if (matches.length === 0) return <NumCell />;

            const total = matches.reduce((sum, conceptValue) => sum + (conceptValue.value ?? 0), 0);
            // La retencion del concepto se informa como nota de la celda: es
            // parte del importe y sin ella el neto de la fila no cierra.
            const withholding = matches.reduce(
              (sum, conceptValue) =>
                sum + (conceptValue.applyWithholding ? (conceptValue.withholdingAmount ?? 0) : 0),
              0
            );

            return (
              <NumCell
                value={money(total, item)}
                note={withholding ? `−${money(withholding, item)}` : undefined}
              />
            );
          },
        })
      );
  }, [items, sharedCurrency]);

  const columns: TableColumnDefinition<ProtocolConsumption>[] = [
    createTableColumn<ProtocolConsumption>({
      columnId: 'status',
      compare: (a, b) => (props.ProviderId ? a.costStatus - b.costStatus : a.status - b.status),
      renderHeaderCell: () => <span>{t('consumptionList.headers.status')}</span>,
      renderCell: (item: ProtocolConsumption) => {
        if (item.ischangingStatus || item.isDeleting || item.isSaving) {
          return <Spinner size="extra-tiny" />;
        }

        const status = ConsumptionListFunctions.GetStatusDescription(props.ProviderId ? item.costStatus : item.status);

        // El punto de color va siempre acompanado del texto: el color solo no
        // comunica el estado a quien no distingue matices.
        return (
          <span className="cgrid-status" title={status.description}>
            <span className={'status-mark ' + status.class}></span>
            <span className="cgrid-status-label">{status.description}</span>
          </span>
        );
      },
    }),
    createTableColumn<ProtocolConsumption>({
      columnId: 'concept',
      compare: (a, b) => a.serviceName.localeCompare(b.serviceName),
      renderHeaderCell: () => <span>{t('consumptionList.headers.concept')}</span>,
      renderCell: (item: ProtocolConsumption) => <div className="cgrid-concept">{item.serviceName}</div>,
    }),
    createTableColumn<ProtocolConsumption>({
      columnId: 'amount',
      compare: (a, b) => getNetAmountForGrid(a) - getNetAmountForGrid(b),
      renderHeaderCell: () => <span>{t('consumptionList.headers.amount')}</span>,
      renderCell: (item: ProtocolConsumption) => (
        <NumCell value={money(getNetAmountForGrid(item), item)} tone="strong" />
      ),
    }),
    ...conceptColumns,
    createTableColumn<ProtocolConsumption>({
      columnId: 'withholding',
      compare: (a, b) => (a.withHolingAmount ?? 0) - (b.withHolingAmount ?? 0),
      renderHeaderCell: () => <span>{t('consumptionList.headers.withholding')}</span>,
      renderCell: (item: ProtocolConsumption) => (
        <NumCell
          value={item.withHolingAmount ? `−${money(item.withHolingAmount, item)}` : undefined}
          note={item.withHolingAmount ? fmtPercent(item.withHolingPercentage) : undefined}
          tone="negative"
        />
      ),
    }),
  ];

  const Forms = (
    <>
      {selectedConsumption && (
        <ConsumptionDetailsCompoment
          ProtocolId={selectedConsumption.protocolId}
          ParentProtocolConsumptionId={props.CosnumptionId}
          ProtocolConsumptionId={selectedConsumption.id}
          visitId={undefined}
          isOpen={isInformationPanelOpen}
          onClose={onInformationPanelDismiss}
        />
      )}
    </>
  );

  return (
    <>
      <div className="childGrid">
        <div className="childGrid-toolbar">
          <span className="childGrid-count">
            {items.length} {items.length === 1 ? 'concepto' : 'conceptos'}
          </span>
          {sharedCurrency && <span className="childGrid-currency">Importes en {sharedCurrency}</span>}
        </div>

        {/* Solo se reemplaza la grilla por el spinner cuando todavia no hay nada
            que mostrar; una recarga en segundo plano no vacia la seccion. */}
        {items.length === 0 && props.isLoading && (
          <div className="childGrid-state">
            <Spinner size="tiny" label={t('childConsumptionList.loading')} />
          </div>
        )}

        {items.length === 0 && !props.isLoading && (
          <div className="childGrid-state">{t('childConsumptionList.empty')}</div>
        )}

        {items.length > 0 && (
          <div className="childGrid-scroll">
            <DataGrid
              items={items}
              columns={columns}
              className="childDataGrid"
              size="small"
              sortable
            >
              <DataGridHeader>
                <DataGridRow>
                  {(column) => (
                    <DataGridHeaderCell className={`cgrid-cell cgrid-cell--${column.columnId}`}>
                      {column.renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<ProtocolConsumption>>
                {({ item, rowId }) => (
                  <DataGridRow<ProtocolConsumption>
                    key={rowId}
                    // El click abre el detalle. Va en la fila y no en cada celda:
                    // un unico handler, y Enter sobre la celda enfocada burbujea hasta aca.
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      openConsumption(item);
                    }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        openConsumption(item);
                      }
                    }}
                  >
                    {(column) => (
                      <DataGridCell className={`cgrid-cell cgrid-cell--${column.columnId}`}>
                        {column.renderCell(item)}
                      </DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          </div>
        )}
      </div>
      {Forms}
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ChildConsumptionListComponent as any);
